/**
 * Etkinlik durumu — tek kaynak (liste kolonu + sayfa filtreleri ile uyumlu).
 *
 * API list filtreleri (events.service):
 *   upcoming → startdate >= simdi - 7s
 *   active   → startdate <= simdi <= enddate
 *   past     → enddate < simdi
 *
 * Tablo Status kolonu (bu dosya):
 *   isCancelled → Cancelled
 *   !isActive → Pending
 *   simdi ∈ [start, end] → Happening now!
 *   simdi > end → Recently Ended (veya Past sayfasinda "Ended")
 *   simdi < start → Upcoming
 */
import moment from 'moment';
import { toJsDate } from './dateUtils';

export const EVENT_STATUS = {
  CANCELLED: 'cancelled',
  PENDING: 'pending',
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
  ENDED: 'ended',
  UNKNOWN: 'unknown',
};

/** API ?status= query degerleri */
export const API_STATUS_FILTER = {
  UPCOMING: 'upcoming',
  ACTIVE: 'active',
  PAST: 'past',
};

const DISPLAY = {
  [EVENT_STATUS.CANCELLED]: {
    label: 'Cancelled',
    bgColor: '#d32f2f',
    color: '#fff',
  },
  [EVENT_STATUS.PENDING]: {
    label: 'Pending',
    bgColor: '#6a1b9a',
    color: '#fff',
  },
  [EVENT_STATUS.UPCOMING]: {
    label: 'Upcoming',
    bgColor: '#2e7d32',
    color: '#fff',
  },
  [EVENT_STATUS.ACTIVE]: {
    label: 'Happening now!',
    bgColor: '#ed6c02',
    color: '#fff',
  },
  [EVENT_STATUS.ENDED]: {
    label: 'Recently Ended',
    bgColor: '#757575',
    color: '#fff',
  },
  [EVENT_STATUS.UNKNOWN]: {
    label: 'Unknown',
    bgColor: '#9e9e9e',
    color: '#fff',
  },
};

/**
 * @param {Object} event
 * @param {{ endedLabel?: string }} options
 * @returns {{ key: string, label: string, bgColor: string, color: string }}
 */
export const computeEventStatus = (event, options = {}) => {
  const endedLabel = options.endedLabel ?? DISPLAY[EVENT_STATUS.ENDED].label;

  if (!event) {
    return { key: EVENT_STATUS.UNKNOWN, ...DISPLAY[EVENT_STATUS.UNKNOWN] };
  }

  if (event.isCancelled) {
    return { key: EVENT_STATUS.CANCELLED, ...DISPLAY[EVENT_STATUS.CANCELLED] };
  }

  // Yayina alinmamis → Bekleyen (tarih rozetinden once)
  if (event.isActive === false) {
    return { key: EVENT_STATUS.PENDING, ...DISPLAY[EVENT_STATUS.PENDING] };
  }

  const start = toJsDate(event.startdate);
  const end = toJsDate(event.enddate);

  if (!start || !end) {
    return { key: EVENT_STATUS.UNKNOWN, ...DISPLAY[EVENT_STATUS.UNKNOWN] };
  }

  const now = moment();
  const startDate = moment(start);
  const endDate = moment(end);

  if (now.isBetween(startDate, endDate, undefined, '[]')) {
    return { key: EVENT_STATUS.ACTIVE, ...DISPLAY[EVENT_STATUS.ACTIVE] };
  }

  if (now.isAfter(endDate)) {
    return {
      key: EVENT_STATUS.ENDED,
      label: endedLabel,
      bgColor: DISPLAY[EVENT_STATUS.ENDED].bgColor,
      color: DISPLAY[EVENT_STATUS.ENDED].color,
    };
  }

  if (now.isBefore(startDate)) {
    return { key: EVENT_STATUS.UPCOMING, ...DISPLAY[EVENT_STATUS.UPCOMING] };
  }

  return { key: EVENT_STATUS.UNKNOWN, ...DISPLAY[EVENT_STATUS.UNKNOWN] };
};

/** Liste API filtresi ile ayni mantik mi (debug / tooltip icin) */
export const describeStatusRules = () =>
  [
    'Cancelled: isCancelled = true',
    'Pending: isActive = false (ve iptal degil)',
    'Happening now!: startdate ≤ now ≤ enddate',
    'Recently Ended: enddate < now',
    'Upcoming: startdate > now',
    'Sayfa filtreleri: Upcoming / Active Events / Past Events ayri API status parametresi kullanir.',
  ].join('\n');

const eventStatusUtils = {
  EVENT_STATUS,
  API_STATUS_FILTER,
  computeEventStatus,
  describeStatusRules,
};

export default eventStatusUtils;
